export interface CardLike {
  id: number
  cardImageId: string
  name: string
  imageUrl: string | null
  marketPrice?: number | null
  cheapestVariantId?: number | null
  cheapestVariantPrice?: number | null
}

export interface BuylistEntry {
  cardId: number
  quantity: number
}

export interface MissingCard {
  card: { id: number; name: string; imageUrl: string | null }
  needed: number
  ownedExact: number
  ownedTotal: number
}

export interface CollectionStatus {
  ownsCompleteDeck: boolean
  totalDeckCost: number
  missingCostExact: number
  missingCostWithSubs: number
  missingCostCheapest: number
  buylistExact: BuylistEntry[]
  buylistWithSubs: BuylistEntry[]
  buylistCheapest: BuylistEntry[]
  missingCards: MissingCard[]
}

export interface DeckLine {
  card: CardLike
  quantity: number
}

type OwnedByBase = Record<string, Array<{ cardId: number; quantity: number }>>

/**
 * Tallies what a deck costs and what's missing from the collection. The leader
 * is just a one-copy line processed first, so callers pass `[leaderLine, ...cards]`.
 * "Exact" counts only the printing in the deck, "with subs" accepts any printing
 * of the same base card, and "cheapest" re-prices the shortfall at the cheapest variant.
 */
export function computeCollectionStatus(byBase: OwnedByBase, lines: DeckLine[]): CollectionStatus {
  const status: CollectionStatus = {
    ownsCompleteDeck: true,
    totalDeckCost: 0,
    missingCostExact: 0,
    missingCostWithSubs: 0,
    missingCostCheapest: 0,
    buylistExact: [],
    buylistWithSubs: [],
    buylistCheapest: [],
    missingCards: [],
  }

  for (const { card, quantity } of lines) {
    const price = card.marketPrice || 0
    status.totalDeckCost += price * quantity

    const ownedVariants = byBase[card.cardImageId] || []
    const ownedExact = ownedVariants.find((v) => v.cardId === card.id)?.quantity || 0
    const ownedTotal = ownedVariants.reduce((sum, v) => sum + v.quantity, 0)

    const shortfallExact = Math.max(0, quantity - ownedExact)
    const shortfallWithSubs = Math.max(0, quantity - ownedTotal)

    if (shortfallExact > 0) {
      status.missingCostExact += price * shortfallExact
      status.buylistExact.push({ cardId: card.id, quantity: shortfallExact })
    }

    if (shortfallWithSubs > 0) {
      status.missingCostWithSubs += price * shortfallWithSubs
      status.buylistWithSubs.push({ cardId: card.id, quantity: shortfallWithSubs })

      const cheapestPrice = card.cheapestVariantPrice ?? price
      status.missingCostCheapest += cheapestPrice * shortfallWithSubs
      status.buylistCheapest.push({ cardId: card.cheapestVariantId ?? card.id, quantity: shortfallWithSubs })

      status.missingCards.push({
        card: { id: card.id, name: card.name, imageUrl: card.imageUrl },
        needed: quantity,
        ownedExact,
        ownedTotal,
      })
    }
  }

  status.ownsCompleteDeck = status.missingCards.length === 0
  return status
}
